// eTAMS — Approval inbox (PDF page 8).
// Handles travel requests (manager → HOD → booking desk), expense claims
// (manager/HOD → finance, with audit checklist, amount revision, return for
// correction, reject) and travel advances (manager → accounts, amount
// revision, reject) — every action the previous Approvals page offered.
import React, { useEffect, useMemo, useState } from 'react';
import { expenseAPI, etamsAPI, openExpenseAttachment } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { ROLE_CONFIG } from '../../utils/roles';
import { notify } from '../../utils/notify';
import {
  PageShell, Stat, Card, Badge, Note, Field, Input, Textarea, Check, Empty, Loading, Spin, FileChip, Tabs, VFlow,
  money, pad2, fmtRange, fmtDate, fmtDateTime, promptText, useQueryParam,
} from '../../components/ui';
import { attachmentsOf, travelStatus } from '../../utils/etams';

const CHECKS = [
  ['sanctionVerified', 'Travel sanction verified'], ['suppliedMealsExcluded', 'Supplied meals excluded'],
  ['billsGstChecked', 'Bills and GST checked'], ['advanceReconciled', 'Advance reconciled'],
  ['policyLimitsReviewed', 'Policy limits reviewed'], ['tourReportReceived', 'Tour report received'],
];

function ExpenseApprovals() {
  const { user } = useAuth();
  const focus = useQueryParam('focus');
  const [items, setItems] = useState({ travel: [], claims: [], advances: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [sel, setSel] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [checks, setChecks] = useState({});
  const [amount, setAmount] = useState('');
  const [booking, setBooking] = useState('');
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, a, c] = await Promise.all([
        etamsAPI.listTravel({ scope: 'approvals' }).catch(() => ({ requests: [] })),
        expenseAPI.listAdvances({ limit: 200 }),
        expenseAPI.listClaims({ limit: 200 }),
      ]);
      // The server sets canAct from the assignment (Reporting Manager / HOD /
      // Finance on the employee's profile, or their delegate) — so an approver
      // sees the action buttons whatever their own account role is.
      const canAdv = (r) => {
        if (typeof r.canAct === 'boolean') return r.canAct;
        if (user.role === 'admin') return true;
        if ((user.role === 'manager' || user.role === 'hod') && r.status === 'PENDING_MANAGER') return String(r.assignedManagerId?._id || r.assignedManagerId) === String(user.id);
        if (user.role === 'finance' && r.status === 'PENDING_FINANCE') return String(r.assignedFinanceId?._id || r.assignedFinanceId) === String(user.id);
        return false;
      };
      const canClaim = (r) => {
        if (typeof r.canAct === 'boolean') return r.canAct;
        if (user.role === 'admin') return true;
        const firstRole = r.assignedFirstApproverRole === 'HOD' ? 'hod' : 'manager';
        if (user.role === firstRole && r.status === 'SUBMITTED') return true;
        if (user.role === 'finance' && r.status === 'MANAGER_APPROVED') return true;
        return false;
      };
      const next = {
        travel: (t.requests || []).filter((x) => x.canAct).map((x) => ({ kind: 'travel', id: x._id, rec: x })),
        advances: (a.requests || []).filter((r) => ['PENDING_MANAGER', 'PENDING_FINANCE'].includes(r.status) && canAdv(r)).map((x) => ({ kind: 'advance', id: x._id, rec: x })),
        claims: (c.claims || []).filter((r) => ['SUBMITTED', 'MANAGER_APPROVED'].includes(r.status) && canClaim(r)).map((x) => ({ kind: 'claim', id: x._id, rec: x })),
      };
      setItems(next);
      const all = [...next.travel, ...next.claims, ...next.advances];
      setSel((prev) => all.find((i) => i.id === (prev?.id || focus)) || all[0] || null);
    } catch (e) {
      notify.error(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Deep link (?focus=<id>) from email / bell / My Approvals while already on this page
  useEffect(() => {
    if (!focus) return;
    const hit = [...items.travel, ...items.claims, ...items.advances].find((i) => i.id === focus);
    if (hit) { setTab('all'); setSel(hit); }
  }, [focus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setRemarks(''); setChecks({}); setBooking('');
    if (sel?.kind === 'claim') setAmount(String(sel.rec.currentAmount ?? sel.rec.netPayable ?? ''));
    else if (sel?.kind === 'advance') setAmount(String(sel.rec.currentAmount ?? sel.rec.advanceAmountRequested ?? ''));
    else setAmount('');
  }, [sel?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = useMemo(() => {
    const all = [...items.travel, ...items.claims, ...items.advances];
    return tab === 'all' ? all : all.filter((i) => i.kind === tab);
  }, [items, tab]);
  const claimValue = items.claims.reduce((s, i) => s + (i.rec.currentAmount ?? i.rec.totalExpenses ?? 0), 0);

  const approve = async () => {
    if (!sel) return;
    const r = sel.rec;
    let note = remarks;
    if (sel.kind === 'claim' && CHECKS.some(([k]) => !checks[k])) {
      const ok = await promptText({ title: 'Audit checklist incomplete', label: 'Reason for approving without all checks', confirmText: 'Approve anyway', color: '#d08a1c' });
      if (ok === null) return;
      note = `${note}${note ? ' · ' : ''}Checklist exception: ${ok}`;
    }
    setActing('approve');
    try {
      if (sel.kind === 'travel') {
        await etamsAPI.actTravel(r._id, { action: 'approve', remarks: note, booking: r.status === 'PENDING_ADMIN' && booking ? { reference: booking } : undefined });
      } else if (sel.kind === 'claim') {
        const revised = Number(amount);
        const base = r.currentAmount ?? r.netPayable;
        if (amount !== '' && revised > r.netPayable) { notify.error('Approved amount cannot exceed the claimed net payable'); setActing(null); return; }
        if (amount !== '' && revised !== base && !note.trim()) { notify.error('Add reviewer remarks explaining the revised amount'); setActing(null); return; }
        await expenseAPI.approveClaim(r._id, { remarks: note || 'Approved', revisedAmount: amount === '' ? undefined : revised, auditChecklist: checks });
      } else {
        const revised = Number(amount);
        if (amount !== '' && revised > r.advanceAmountRequested) { notify.error('Amount cannot exceed the requested amount'); setActing(null); return; }
        await expenseAPI.approveAdvance(r._id, { remarks: note || 'Approved', revisedAmount: amount === '' ? undefined : revised });
      }
      notify.success('Approved');
      setSel(null);
      await load();
    } catch (e) { notify.error(e.message || 'Failed'); } finally { setActing(null); }
  };

  const returnOrReject = async (action) => {
    if (!sel) return;
    let text = remarks.trim();
    if (!text) {
      text = await promptText({ title: action === 'reject' ? 'Reject' : 'Return for correction', label: 'Remarks', confirmText: action === 'reject' ? 'Reject' : 'Return', color: action === 'reject' ? '#c43c31' : '#1479bd' });
      if (text === null) return;
    }
    setActing(action);
    try {
      const r = sel.rec;
      if (sel.kind === 'travel') await etamsAPI.actTravel(r._id, { action, remarks: text });
      else if (sel.kind === 'claim') { if (action === 'reject') await expenseAPI.rejectClaim(r._id, text); else await etamsAPI.returnClaim(r._id, text); }
      else await expenseAPI.rejectAdvance(r._id, text);
      notify.success(action === 'reject' ? 'Rejected' : 'Returned to the employee for correction');
      setSel(null);
      await load();
    } catch (e) { notify.error(e.message || 'Failed'); } finally { setActing(null); }
  };

  const reviewer = ROLE_CONFIG[user?.role]?.label || 'Reviewer';
  const itemView = (i) => {
    const r = i.rec;
    if (i.kind === 'travel') return { icon: 'bi-airplane', title: `${r.destination} travel request`, ref: r.requestNo, loc: `${r.origin} → ${r.destination}`, dates: fmtRange(r.departureDate, r.returnDate), amount: r.estimatedTotal, stage: travelStatus(r).stage };
    if (i.kind === 'claim') return { icon: 'bi-file-earmark-text', title: `${r.reportTitle || r.purposeOfTravel}`, ref: r.claimId, loc: r.travelLocation, dates: fmtRange(r.travelStartDate, r.travelEndDate), amount: r.currentAmount ?? r.totalExpenses, stage: r.status === 'SUBMITTED' ? 'Manager' : 'Finance' };
    return { icon: 'bi-wallet2', title: `Advance — ${r.travelLocation}`, ref: r.requestId, loc: r.travelLocation, dates: fmtRange(r.travelStartDate, r.travelEndDate), amount: r.currentAmount ?? r.advanceAmountRequested, stage: r.status === 'PENDING_MANAGER' ? 'Manager' : 'Accounts' };
  };

  const r = sel?.rec;
  const kindLabel = { travel: 'Travel request', claim: 'Expense claim', advance: 'Travel advance' }[sel?.kind];

  return (
    <PageShell section="Travel & Expenses">
      <div className="tse-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 8 }}>
        <h1 className="tse-title" style={{ marginTop: 10 }}>Approval inbox <span className="tse-chip-note">{reviewer} review</span></h1>
        <div className="tse-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))', gap: 8, flex: 1, maxWidth: 860 }}>
          <Stat compact tint="blue" icon="bi-airplane" tone="blue" label="Travel requests" value={pad2(items.travel.length)} onClick={() => setTab('travel')} />
          <Stat compact tint="green" icon="bi-file-earmark-text" tone="green" label="Expense claims" value={pad2(items.claims.length)} onClick={() => setTab('claim')} />
          <Stat compact tint="amber" icon="bi-currency-rupee" tone="amber" label="Claim value" value={money(claimValue)} />
          <Stat compact tint="purple" icon="bi-wallet2" tone="purple" label="Advance requests" value={pad2(items.advances.length)} onClick={() => setTab('advance')} />
        </div>
      </div>

      <div className="tse-split left-side" style={{ gridTemplateColumns: 'minmax(300px, 0.9fr) minmax(0, 1.6fr)' }}>
        <Card navy bodyClass={null} title={`Pending for review (${list.length})`} style={{ minHeight: 560 }}>
          <div style={{ padding: '7px 8px 0' }}><Tabs value={tab} onChange={setTab} tabs={[{ key: 'all', label: 'All' }, { key: 'travel', label: 'Travel' }, { key: 'claim', label: 'Claims' }, { key: 'advance', label: 'Advances' }]} /></div>
          <div style={{ padding: 7 }}>
            {loading ? <Loading /> : !list.length ? <Empty icon="bi-check2-all" title="All caught up" sub="Nothing is waiting for your review." /> : list.map((i) => {
              const v = itemView(i);
              return (
                <button key={i.id} type="button" className={`tse-list-item ${sel?.id === i.id ? 'selected' : ''}`} onClick={() => setSel(i)}>
                  <span className="ic blue"><i className={`bi ${v.icon}`} /></span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="t tse-ellipsis">{v.title}</div>
                    <div className="s">{v.ref}</div>
                    <div className="m">{v.loc}</div>
                    <div className="m"><i className="bi bi-calendar3" /> {v.dates}</div>
                  </div>
                  <div className="r">
                    <span className="amt">{money(v.amount)}</span>
                    <Badge tone="pending" pill>Pending</Badge>
                    <span className="tse-small tse-muted">{i.rec.employeeName}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {!sel ? <Card><Empty icon="bi-inbox" title="Select an item to review" /></Card> : (
          <div className="tse-card">
            <div className="tse-card-head" style={{ background: '#173b5f', color: '#fff', borderRadius: '10px 10px 0 0', padding: '10px 15px' }}>
              <div style={{ fontSize: '1.032rem' }}>{itemView(sel).title}</div>
              <div style={{ textAlign: 'right', fontSize: '0.791rem', fontWeight: 400 }}>{kindLabel}<br /><strong>{itemView(sel).ref}</strong></div>
            </div>
            <div className="tse-meta-strip">
              <div><div className="k">Employee</div><div className="v">{r.employeeName}</div></div>
              <div><div className="k">Project</div><div className="v">{r.projectCode || '—'}</div></div>
              <div><div className="k">Location</div><div className="v">{sel.kind === 'travel' ? r.destination : r.travelLocation}</div></div>
              <div><div className="k">Travel period</div><div className="v">{itemView(sel).dates}</div></div>
            </div>
            <div className="tse-card-body">
              {sel.kind === 'claim' && (
                <div className="tse-amounts mb-18">
                  <div><div className="k">Employee claim</div><div className="v">{money(r.totalExpenses)}</div></div>
                  <div><div className="k">Advance received</div><div className="v">{money(r.advanceReceived)}</div></div>
                  <div className="hl"><div className="k">Net reimbursement</div><div className="v">{money(r.currentAmount ?? r.netPayable)}</div></div>
                </div>
              )}
              {sel.kind === 'travel' && (
                <div className="tse-amounts mb-18">
                  <div><div className="k">Estimated total</div><div className="v">{money(r.estimatedTotal)}</div></div>
                  <div><div className="k">Advance ceiling</div><div className="v">{money(r.advanceCeiling)}</div></div>
                  <div className="hl"><div className="k">Duration</div><div className="v">{r.totalDays || '—'} days</div></div>
                </div>
              )}
              {sel.kind === 'advance' && (
                <div className="tse-amounts mb-18">
                  <div><div className="k">Requested</div><div className="v">{money(r.advanceAmountRequested)}</div></div>
                  <div><div className="k">Estimated tour cost</div><div className="v">{money(r.estimatedTotalExpense)}</div></div>
                  <div className="hl"><div className="k">One-third ceiling</div><div className="v">{money(Math.floor((r.estimatedTotalExpense || 0) / 3))}</div></div>
                </div>
              )}

              {sel.kind === 'travel' && (
                <>
                  {(r.policyFlags || []).length > 0 && <Note tone="warn" title="Policy checks" className="mb-18">{r.policyFlags.join(' · ')}</Note>}
                  <div className="tse-table-wrap mb-18"><table className="tse-table compact bordered">
                    <thead><tr><th>Mode</th><th>Fare</th><th>Hotel</th><th>Food</th><th>OPE</th><th>Conveyance</th><th>Other</th></tr></thead>
                    <tbody><tr><td>{r.modeOfTravel}</td>{['fare', 'hotel', 'food', 'ope', 'conveyance', 'other'].map((k) => <td key={k}>{money(r.costs?.[k])}</td>)}</tr></tbody>
                  </table></div>
                  <div className="tse-small mb-18"><span className="tse-muted">Purpose:</span> {r.purpose} · <span className="tse-muted">Band</span> {r.policyBand || '—'} · <span className="tse-muted">Tier</span> {r.cityTier}</div>
                  {r.status === 'PENDING_ADMIN' && <Field label="Booking reference (booking desk)" className="mb-18"><Input value={booking} onChange={(e) => setBooking(e.target.value)} placeholder="PNR / ticket / hotel confirmation" /></Field>}
                </>
              )}

              {sel.kind === 'claim' && (
                <>
                  <div className="tse-table-wrap mb-18"><table className="tse-table compact bordered">
                    <thead><tr><th>Category</th><th>Bill ref.</th><th>Paid by</th><th className="num">Base</th><th className="num">GST</th><th className="num">Total</th></tr></thead>
                    <tbody>{(r.expenses || []).map((e, idx) => <tr key={idx}><td>{e.expenseType}</td><td>{e.billReference || e.description || '—'}</td><td>{e.paidBy || 'Employee'}</td><td className="num">{money(e.amount)}</td><td className="num">{money(e.gst)}</td><td className="num strong">{money(e.total)}</td></tr>)}</tbody>
                  </table></div>
                  <div className="tse-strong" style={{ fontSize: '0.929rem', marginBottom: 10 }}>Audit checklist</div>
                  <div className="tse-checklist mb-18">
                    {CHECKS.map(([k, l]) => <Check key={k} checked={checks[k]} onChange={(v) => setChecks((p) => ({ ...p, [k]: v }))} label={l} />)}
                  </div>
                </>
              )}

              {sel.kind === 'advance' && r.purposeOfAdvance && <div className="tse-small mb-18"><span className="tse-muted">Reason:</span> {r.purposeOfAdvance} · <span className="tse-muted">Needed by</span> {fmtDate(r.dateRequired)} · <span className="tse-muted">Mode</span> {r.preferredMode || '—'}</div>}

              {attachmentsOf(r).length > 0 && (
                <div className="tse-grid g-2 mb-18">{attachmentsOf(r).map((a, i) => <FileChip key={a.s3Key || i} file={a} onOpen={() => openExpenseAttachment(a).catch(() => notify.error('Could not open attachment'))} />)}</div>
              )}

              {sel.kind !== 'travel' && (
                <Field label={sel.kind === 'claim' ? 'Approve amount (net reimbursement) — change only if revising' : 'Approve amount — change only if revising'} className="mb-18">
                  <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ maxWidth: 260 }} />
                </Field>
              )}

              <hr className="tse-divider" />
              <Field label="Reviewer remarks"><Textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="e.g. Claim looks in order. All supporting documents verified." /></Field>

              {(r.approvalHistory || []).length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div className="tse-label" style={{ marginBottom: 8 }}>Approval trail</div>
                  <VFlow items={r.approvalHistory.map((h) => ({ state: { APPROVED: 'done', SUBMITTED: 'done', REJECTED: 'rejected', RETURNED: 'returned' }[h.action], title: `${h.action.charAt(0)}${h.action.slice(1).toLowerCase()} · ${h.stage || h.role}`, sub: h.remarks, meta: <>{h.approverName || h.approverId?.userName || ''}<br />{fmtDateTime(h.actionAt)}</> }))} />
                </div>
              )}

              <div className="tse-row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <button type="button" className="tse-btn lg danger-outline" disabled={!!acting} onClick={() => returnOrReject('reject')}>{acting === 'reject' ? <Spin /> : null}Reject</button>
                {sel.kind !== 'advance' && <button type="button" className="tse-btn lg outline" disabled={!!acting} onClick={() => returnOrReject('return')}>{acting === 'return' ? <Spin /> : null}Return for correction</button>}
                <button type="button" className="tse-btn lg" style={{ minWidth: 180 }} disabled={!!acting} onClick={approve}>{acting === 'approve' ? <Spin /> : null}{sel.kind === 'claim' ? 'Approve claim' : sel.kind === 'travel' ? 'Approve travel' : 'Approve advance'}</button>
              </div>
              <div className="tse-row tse-small tse-muted" style={{ justifyContent: 'flex-end', marginTop: 10, gap: 6 }}><i className="bi bi-info-circle" />Your decision, remarks and timestamp are recorded in the approval trail and the employee is notified.</div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}
export default ExpenseApprovals;
